/**
 * QuotaChip — slim daily-quota indicator for the mobile top bar.
 *
 * iter-93 — surfaces the user's remaining daily "pours" so the
 * upgrade conversation can start BEFORE they hit the 429 wall.
 *
 * Visibility rules:
 *   • remaining > 1                → hidden (no nagging)
 *   • remaining == 1               → soft yellow pill, last-call pulse
 *   • remaining == 0               → red pill "pours done · go unlimited"
 *   • paid tier (plus/premium)     → hidden
 *
 * Fetches `/api/me/quota` on mount + on `quota-changed` window event so
 * Home create-card flow can ping it after a successful pour.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";

export default function QuotaChip() {
    const [quota, setQuota] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        const fetchQuota = async () => {
            try {
                const { data } = await axios.get(`${API}/me/quota`, { withCredentials: true });
                if (!cancelled) setQuota(data);
            } catch {
                // silent — quota is non-critical UI
            }
        };
        fetchQuota();
        const onChange = () => fetchQuota();
        window.addEventListener("quota-changed", onChange);
        return () => {
            cancelled = true;
            window.removeEventListener("quota-changed", onChange);
        };
    }, []);

    if (!quota) return null;
    // iter-101 — also hide for plus/premium (legacy creator) tiers, not just "free"
    if (["plus", "premium", "creator"].includes(quota.tier)) return null;
    const remaining = quota.remaining ?? 0;
    if (remaining > 1) return null;

    const isExhausted = remaining <= 0;

    return (
        <button
            onClick={() => {
                if (isExhausted) navigate("/exhausted");
                else window.dispatchEvent(new CustomEvent("open-premium-modal", { detail: { source: "quota_chip" } }));
            }}
            data-testid={isExhausted ? "quota-chip-exhausted" : "quota-chip-last"}
            className={[
                "px-3 py-1 rounded-full font-sans text-[10px] uppercase tracking-[0.22em] font-medium border transition-all",
                isExhausted
                    ? "bg-rose-500/15 border-rose-400/50 text-rose-100 animate-pulse"
                    : "bg-amber-300/10 border-amber-300/40 text-amber-100",
            ].join(" ")}
        >
            {isExhausted ? "pours done →" : "1 left · upgrade"}
        </button>
    );
}
